import { Component } from '@angular/core';
import { HuntApiService, ScheduleRequest, ScheduleResult } from '../hunt-api.service';
import { accessMessage } from '../hunt-errors';

const ALL_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/** "1-10, 15" -> [1..10, 15]; "" -> undefined (all bones). Throws on bad input. */
export function parseRange(spec: string): number[] | undefined {
  if (!spec || !spec.trim()) return undefined;
  const out: number[] = [];
  for (const part of spec.split(',').map((p) => p.trim()).filter(Boolean)) {
    const m = /^(\d+)(?:\s*-\s*(\d+))?$/.exec(part);
    if (!m) throw new Error(`"${part}" isn't a bone number or range`);
    const a = Number(m[1]), b = Number(m[2] || m[1]);
    if (b < a || b - a > 5000) throw new Error(`"${part}" isn't a valid range`);
    for (let n = a; n <= b; n++) out.push(n);
  }
  return out;
}

@Component({
  selector: 'app-hunt-schedule',
  templateUrl: './hunt-schedule.component.html',
  styleUrls: ['./hunt-schedule.component.scss'],
})
export class HuntScheduleComponent {
  readonly days = ALL_DAYS;
  mode: 'every' | 'times' = 'every';
  form = {
    from: new Date(Date.now() + 86400000).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
    every: 120,
    windowStart: '10:00',
    windowEnd: '20:00',
    times: '11:00, 16:00',
    duration: 90,
    days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
    bones: '',
  };
  unscheduleBones = '';
  plan: ScheduleResult | null = null;
  busy = false;
  error = '';
  notice = '';

  constructor(private api: HuntApiService) {}

  toggleDay(d: string) {
    const i = this.form.days.indexOf(d);
    if (i === -1) this.form.days.push(d); else this.form.days.splice(i, 1);
    this.plan = null;
  }

  private request(dryRun: boolean): ScheduleRequest {
    const f = this.form;
    const req: ScheduleRequest = { from: f.from, duration: Number(f.duration), days: f.days.length === 7 ? undefined : f.days, bones: parseRange(f.bones), dryRun };
    if (this.mode === 'every') { req.every = Number(f.every); req.window = `${f.windowStart}-${f.windowEnd}`; }
    else req.times = f.times.split(',').map((t) => t.trim()).filter(Boolean);
    return req;
  }

  previewPlan() { this.run(true); }
  savePlan() {
    if (!this.plan || !confirm(`Save ${this.plan.scheduled.length} slots?`)) return;
    this.run(false);
  }

  private run(dryRun: boolean) {
    this.error = '';
    this.notice = '';
    if (!this.form.days.length) { this.error = 'Pick at least one day.'; return; }
    let req: ScheduleRequest;
    try { req = this.request(dryRun); } catch (e) { this.error = e.message; return; }
    this.busy = true;
    this.api.schedule(req).subscribe(
      (r) => {
        this.busy = false;
        if (dryRun) this.plan = r;
        else { this.plan = null; this.notice = `${r.scheduled.length} bones scheduled.`; }
      },
      (e) => { this.busy = false; this.error = accessMessage(e); },
    );
  }

  unschedule() {
    let bones: number[] | undefined;
    try { bones = parseRange(this.unscheduleBones); } catch (e) { this.error = e.message; return; }
    if (!confirm(bones ? `Clear the slots of bones ${this.unscheduleBones}?` : 'Clear the slots of every bone that hasn\'t started?')) return;
    this.busy = true;
    this.api.unschedule(bones).subscribe(
      (r) => { this.busy = false; this.notice = `${r.unscheduled} bones unscheduled.`; },
      (e) => { this.busy = false; this.error = accessMessage(e); },
    );
  }

  span() {
    if (!this.plan || !this.plan.scheduled.length) return null;
    const s = this.plan.scheduled;
    return { first: s[0].opensAt, last: s[s.length - 1].closesAt };
  }
}
